'use client';

import { hashKey } from '@zg-rentals/react-query5';
import { usePathname, useSearchParams } from 'next/navigation';
import { createContext, type ReactNode, useCallback, useContext, useEffect, useMemo, useRef } from 'react';

import type { SearchListingsRequest } from '@/src/lib/api';
import { searchKeys } from '@/src/lib/hooks/query/queryKeys';
import { emitTrackViewSrpEvent } from './hotpads/events';

type TrackedLiveSearch = {
  requestKey: string;
  sequence: number;
};

type PendingCanonicalRemount = TrackedLiveSearch & {
  sourceUrl: string;
  targetUrl: string;
};

type SrpPageViewCoordinatorValue = {
  registerCanonicalRemount: (requestKey: string, targetUrl: string) => void;
  trackLiveSearch: (requestKey: string, isAgentic: boolean) => void;
  trackMount: (isAgentic: boolean) => void;
};

const SrpPageViewCoordinatorContext = createContext<SrpPageViewCoordinatorValue | null>(null);

function getCurrentUrl(): string {
  return typeof window === 'undefined' ? '' : `${window.location.pathname}${window.location.search}`;
}

function normalizeUrl(url: string): string {
  if (typeof window === 'undefined') return url;
  const parsedUrl = new URL(url, window.location.href);
  return `${parsedUrl.pathname}${parsedUrl.search}`;
}

export function getSrpPageViewRequestKey(request: SearchListingsRequest): string {
  return hashKey(searchKeys.listings(request));
}

export function SrpPageViewCoordinatorProvider({ children }: { children: ReactNode }) {
  const pathname = usePathname();
  const searchParams = useSearchParams();
  const search = searchParams.toString();
  const nextSequenceRef = useRef(0);
  const trackedLiveSearchRef = useRef<TrackedLiveSearch | null>(null);
  const pendingCanonicalRemountRef = useRef<PendingCanonicalRemount | null>(null);

  const trackMount = useCallback((isAgentic: boolean) => {
    const pendingCanonicalRemount = pendingCanonicalRemountRef.current;
    const trackedLiveSearch = trackedLiveSearchRef.current;
    const shouldConsumeCanonicalRemount =
      pendingCanonicalRemount?.targetUrl === getCurrentUrl() &&
      pendingCanonicalRemount.sequence === trackedLiveSearch?.sequence;

    pendingCanonicalRemountRef.current = null;
    trackedLiveSearchRef.current = null;

    if (shouldConsumeCanonicalRemount) return;
    emitTrackViewSrpEvent({ isAgentic });
  }, []);

  const trackLiveSearch = useCallback((requestKey: string, isAgentic: boolean) => {
    nextSequenceRef.current += 1;
    trackedLiveSearchRef.current = { requestKey, sequence: nextSequenceRef.current };
    pendingCanonicalRemountRef.current = null;
    emitTrackViewSrpEvent({ isAgentic });
  }, []);

  const registerCanonicalRemount = useCallback((requestKey: string, targetUrl: string) => {
    const trackedLiveSearch = trackedLiveSearchRef.current;
    const sourceUrl = getCurrentUrl();
    const normalizedTargetUrl = normalizeUrl(targetUrl);

    if (!trackedLiveSearch || trackedLiveSearch.requestKey !== requestKey || sourceUrl === normalizedTargetUrl) {
      pendingCanonicalRemountRef.current = null;
      return;
    }

    pendingCanonicalRemountRef.current = {
      ...trackedLiveSearch,
      sourceUrl,
      targetUrl: normalizedTargetUrl,
    };
  }, []);

  useEffect(() => {
    const pendingCanonicalRemount = pendingCanonicalRemountRef.current;
    if (!pendingCanonicalRemount) return;

    const currentUrl = getCurrentUrl();
    if (currentUrl !== pendingCanonicalRemount.sourceUrl && currentUrl !== pendingCanonicalRemount.targetUrl) {
      pendingCanonicalRemountRef.current = null;
      trackedLiveSearchRef.current = null;
      return;
    }

    if (currentUrl !== pendingCanonicalRemount.targetUrl) return;
    queueMicrotask(() => {
      if (pendingCanonicalRemountRef.current?.sequence !== pendingCanonicalRemount.sequence) return;
      pendingCanonicalRemountRef.current = null;
      trackedLiveSearchRef.current = null;
    });
  }, [pathname, search]);

  const value = useMemo(
    () => ({ registerCanonicalRemount, trackLiveSearch, trackMount }),
    [registerCanonicalRemount, trackLiveSearch, trackMount],
  );

  return <SrpPageViewCoordinatorContext.Provider value={value}>{children}</SrpPageViewCoordinatorContext.Provider>;
}

export function useSrpPageViewCoordinator(): SrpPageViewCoordinatorValue {
  const coordinator = useContext(SrpPageViewCoordinatorContext);
  if (!coordinator) {
    throw new Error('useSrpPageViewCoordinator must be used within SrpPageViewCoordinatorProvider');
  }
  return coordinator;
}
